import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table, Button, Form, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl, getMediaUrl } from "../../../utils/site";

const getStoredAuthor = () => {
  try {
    const rawUser = localStorage.getItem("user");
    const storedUser = rawUser && rawUser !== "undefined" ? JSON.parse(rawUser) : null;
    return storedUser?.name || storedUser?.email || String(storedUser?.id || "Admin");
  } catch (error) {
    return "Admin";
  }
};

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [author, setAuthor] = useState("");
  const [blogData, setBlogData] = useState({
    title: "",
    content: "",
    category: "Other",
    image_url: "",
    excerpt: "",
    status: "published",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageSource, setImageSource] = useState("url");
  const [error, setError] = useState("");
  const fileInputRef = useRef();

  useEffect(() => {
    setAuthor(getStoredAuthor());
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("/api/blogs"));
      setBlogs(res.data.blogs || res.data || []);
    } catch (err) {
      toast.error("Failed to fetch blogs.");
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = (blog = null) => {
    setModalOpen(!modalOpen);
    if (blog) {
      setEditingBlog(blog);
      setBlogData({
        title: blog.title || "",
        content: blog.content || "",
        category: blog.category || "Other",
        image_url: blog.image_url || "",
        excerpt: blog.excerpt || "",
        status: blog.status || "published",
      });
      setImageSource("url");
      setImageFile(null);
      setImagePreview(blog.image_url || null);
    } else {
      setEditingBlog(null);
      setBlogData({ title: "", content: "", category: "Other", image_url: "", excerpt: "", status: "published" });
      setImageSource("url");
      setImageFile(null);
      setImagePreview(null);
      setError("");
    }
  };

  const handleChange = (e) => setBlogData({ ...blogData, [e.target.name]: e.target.value });
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };
  const handleImageUrlChange = (e) => {
    setBlogData({ ...blogData, image_url: e.target.value });
    setImagePreview(e.target.value || null);
  };

  const handleSaveBlog = async () => {
    const token = localStorage.getItem("token");
    const nextAuthor = author || getStoredAuthor();
    setError("");

    if (imageSource === "url" && !blogData.image_url.trim()) {
      setError("Please provide an image URL or choose upload image.");
      return;
    }
    if (imageSource === "upload" && !imageFile) {
      setError("Please upload an image file or choose image URL.");
      return;
    }
    try {
      if (imageSource === "upload" && imageFile) {
        const formData = new FormData();
        formData.append("title", blogData.title);
        formData.append("content", blogData.content);
        formData.append("author", nextAuthor);
        formData.append("category", blogData.category || "Other");
        if (blogData.excerpt) formData.append("excerpt", blogData.excerpt);
        formData.append("image", imageFile);
        formData.append("status", blogData.status || "published");
        const request = editingBlog
          ? axios.put(getApiUrl(`/api/blogs/${editingBlog.id}`), formData, {
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            })
          : axios.post(getApiUrl("/api/blogs"), formData, {
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            });
        await request;
      } else {
        const payload = {
          title: blogData.title,
          content: blogData.content,
          author: nextAuthor,
          category: blogData.category || "Other",
          image_url: blogData.image_url,
          excerpt: blogData.excerpt,
          status: blogData.status || "published",
        };
        const request = editingBlog
          ? axios.put(getApiUrl(`/api/blogs/${editingBlog.id}`), payload, { headers: { Authorization: `Bearer ${token}` } })
          : axios.post(getApiUrl("/api/blogs"), payload, { headers: { Authorization: `Bearer ${token}` } });
        await request;
      }
      toast.success(editingBlog ? "Blog updated." : "Blog added.");
      fetchBlogs();
      toggleModal();
    } catch (err) {
      const message = err.response?.data?.error || "Failed to save blog.";
      setError(message);
      toast.error(message);
    }
  };

  const handleDeleteBlog = async (id) => {
    const token = localStorage.getItem("token");
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    try {
      await axios.delete(getApiUrl(`/api/blogs/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Blog deleted.");
      fetchBlogs();
    } catch (err) {
      toast.error("Failed to delete blog.");
    }
  };

  return (
    <AdminPageShell
      eyebrow="Editorial management"
      title="Blogs"
      description="Manage Roha editorial content, blog imagery, excerpts, and publishing actions from one polished view."
      actions={
        <Link to="/admin/blogs/add" className="roha-admin-navbar__chip roha-admin-navbar__chip--primary">
          Add blog
        </Link>
      }
    >
      <AdminSurface title="Editorial library" subtitle={`${blogs.length} blog entries`}>
        <div className="roha-admin-table-wrap">
          <Table responsive className="roha-admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Category</th>
                <th>Author</th>
                <th>Image</th>
                <th>Excerpt</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="roha-admin-empty">
                    Loading blogs...
                  </td>
                </tr>
              ) : blogs.length ? (
                blogs.map((blog, index) => (
                  <tr key={blog.id}>
                    <td data-label="#">{index + 1}</td>
                    <td data-label="Title">{blog.title}</td>
                    <td data-label="Category">{blog.category || "Editorial"}</td>
                    <td data-label="Author">{blog.author || "-"}</td>
                    <td data-label="Image">
                      {blog.image_url ? <img src={getMediaUrl(blog.image_url)} alt={blog.title} className="roha-admin-image-thumb" /> : "N/A"}
                    </td>
                    <td data-label="Excerpt">{blog.excerpt || "-"}</td>
                    <td data-label="Status"><span className="roha-admin-pill">{blog.status || "-"}</span></td>
                    <td data-label="Actions">
                      <div className="roha-admin-toolbar">
                        <Button color="warning" size="sm" onClick={() => toggleModal(blog)}>Edit</Button>
                        <Button color="danger" size="sm" onClick={() => handleDeleteBlog(blog.id)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="roha-admin-empty">
                    No blogs found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Modal isOpen={modalOpen} toggle={() => toggleModal()}>
          <ModalHeader toggle={() => toggleModal()}>{editingBlog ? "Edit blog" : "Blog"}</ModalHeader>
          <ModalBody>
            <Form>
              <div className="roha-admin-form-grid roha-admin-form-grid--two">
              <FormGroup>
                <Label>Title</Label>
                <Input type="text" name="title" value={blogData.title} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label>Category</Label>
                <Input type="select" name="category" value={blogData.category} onChange={handleChange}>
                  <option value="Gold">Gold</option>
                  <option value="Silver">Silver</option>
                  <option value="Gemstones">Gemstones</option>
                  <option value="Design Trends">Design Trends</option>
                  <option value="Care & Maintenance">Care & Maintenance</option>
                  <option value="Events">Events</option>
                  <option value="Other">Other</option>
                </Input>
              </FormGroup>
              </div>
              <div className="roha-admin-form-grid roha-admin-form-grid--two">
                <FormGroup>
                  <Label>Excerpt</Label>
                  <Input type="text" name="excerpt" value={blogData.excerpt} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                  <Label>Status</Label>
                  <Input type="select" name="status" value={blogData.status} onChange={handleChange}>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </Input>
                </FormGroup>
              </div>
              <FormGroup>
                <Label>Content</Label>
                <Input type="textarea" name="content" rows="8" value={blogData.content} onChange={handleChange} />
              </FormGroup>
              <div className="roha-admin-toolbar" style={{ marginBottom: 12 }}>
                <label className="roha-admin-pill">
                  <input
                    type="radio"
                    checked={imageSource === "url"}
                    onChange={() => {
                      setImageSource("url");
                      setImageFile(null);
                      setImagePreview(blogData.image_url);
                    }}
                  />
                  Image URL
                </label>
                <label className="roha-admin-pill">
                  <input
                    type="radio"
                    checked={imageSource === "upload"}
                    onChange={() => {
                      setImageSource("upload");
                      setBlogData({ ...blogData, image_url: "" });
                      setImagePreview(null);
                    }}
                  />
                  Upload image
                </label>
              </div>
              {imageSource === "url" ? (
                <FormGroup>
                  <Label>Image URL</Label>
                  <Input type="text" value={blogData.image_url} onChange={handleImageUrlChange} />
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label>Upload image</Label>
                  <Input type="file" accept="image/*" innerRef={fileInputRef} onChange={handleFileChange} />
                </FormGroup>
              )}
              {imagePreview ? <img src={getMediaUrl(imagePreview)} alt="Preview" className="roha-admin-image-thumb" /> : null}
              {error ? <div className="roha-admin-muted" style={{ marginTop: 12 }}>{error}</div> : null}
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleSaveBlog}>Save</Button>
            <Button color="secondary" onClick={() => toggleModal()}>Cancel</Button>
          </ModalFooter>
        </Modal>
      </AdminSurface>
    </AdminPageShell>
  );
};

export default BlogPage;
